/**
 * Phase 35 — the history line's words ("delivered rate: median 91 % ·
 * worst 10 % of days under 78 %"), the chip's ("Use < 78 %"), and how a
 * chip lands in the card: it replaces the condition on the same metric and
 * operator, or adds one — in the metric's stored unit.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenHistoryMetric, DropGenHistoryStat, DropGenHistorySuggestion } from '@trafficpulse/shared';

import type { DraftVerdictRow } from '../../../../drop-generator.types';
import { isTagRow } from '../../../../drop-generator-rule.types';

export const METRIC_WORDS: Record<DropGenHistoryMetric, string> = {
  deliveredRate: 'delivered rate',
  botRate: 'bot rate',
  realCtr: 'real CTR',
  undeliverableRate: 'undeliverable rate',
  ctr: 'CTR',
  cv: 'CV',
};

/** Rates where high is bad: their worst tenth sits at the top. */
const BAD_WHEN_HIGH = new Set<DropGenHistoryMetric>(['botRate', 'undeliverableRate']);

/** A stored value in the operator's percent: 0.912 → "91.2 %", ctr 12.4 → "12.4 %". */
export const pct = (metric: DropGenHistoryMetric, v: number): string => `${Math.round((metric === 'ctr' || metric === 'cv' ? v : v * 100) * 10) / 10} %`;

export function statWords(s: DropGenHistoryStat): string {
  const worst = BAD_WHEN_HIGH.has(s.metric) ? `worst 10 % of days over ${pct(s.metric, s.p90)}` : `worst 10 % of days under ${pct(s.metric, s.p10)}`;
  return `${METRIC_WORDS[s.metric]}: median ${pct(s.metric, s.median)} · ${worst}`;
}

export const chipLabel = (s: DropGenHistorySuggestion): string => `Use ${s.op} ${pct(s.metric, s.value)}`;

/** The chip's condition in the card: same metric + operator replaced, else added. */
export function withSuggestion(rows: DraftVerdictRow[], s: DropGenHistorySuggestion): DraftVerdictRow[] {
  const next: DraftVerdictRow = { metric: s.metric, op: s.op, value: s.value };
  const at = rows.findIndex((r) => !isTagRow(r) && r.metric === s.metric && r.op === s.op && !r.not);
  return at === -1 ? [...rows, next] : rows.map((r, i) => (i === at && !isTagRow(r) ? { ...r, value: s.value } : r));
}

/** The days of one metric for a sparkline's hover: "01/10 91 % · 02/10 89 %". */
export const dayWords = (metric: DropGenHistoryMetric, days: Array<{ runDate: string; values: Partial<Record<DropGenHistoryMetric, number>> }>): string =>
  days
    .filter((d) => d.values[metric] !== undefined)
    .map((d) => `${d.runDate.slice(8, 10)}/${d.runDate.slice(5, 7)} ${pct(metric, d.values[metric] as number)}`)
    .join(' · ');
